import { put } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';

import { type GeneratorResult, takeIf } from '@pressreader/appstore';
import { CollectionContentTypes, CollectionWithId, readLaterCollectionId } from '@pressreader/social-types';

import { addBookmarkAction, addBookmarkDone, addBookmarkFailed } from '../../actions';
import { upsertArticleCollectionsSaga } from '../article.collections/article.collections.loaders';
import { upsertIssuePageSetCollectionSaga } from '../issue.pageset/issue.pageset.loaders';

function* addBookmarkSaga(bookmarkDetails: Parameters<typeof addBookmarkAction>[0]): GeneratorResult<void> {
    yield put(addBookmarkAction(bookmarkDetails));

    const { type } = yield takeIf([addBookmarkDone, addBookmarkFailed], action => action.payload === bookmarkDetails);

    if (type === String(addBookmarkFailed)) {
        throw new Error('Failed to add bookmark');
    }
}

function* addPageSetBookmarkSaga(pageSetId: string, collections: CollectionWithId[]) {
    return yield* upsertContentCollectionsSaga({
        contentId: pageSetId,
        collections,
        contentType: CollectionContentTypes.pageSet,
    });
}

function* upsertContentCollectionsSaga({
    contentId,
    collections,
    contentType,
}: {
    contentId: string;
    collections: CollectionWithId[];
    contentType: CollectionContentTypes;
}) {
    if (!contentId) {
        throw new Error('Content ID is not specified');
    }

    if (isEmpty(collections)) {
        // User collection(s) not specified => Save to default collection 'Read Later'
        collections = [
            {
                id: readLaterCollectionId,
            },
        ];
    }
    if (contentType === CollectionContentTypes.article) {
        yield* upsertArticleCollectionsSaga(contentId, {
            collections,
        });
    } else if (contentType === CollectionContentTypes.pageSet) {
        yield* upsertIssuePageSetCollectionSaga(contentId, collections);
    } else {
        throw new Error('unknown contentType');
    }
}

export { addBookmarkSaga, addPageSetBookmarkSaga, upsertContentCollectionsSaga };
